import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { GeoSearchControl, OpenStreetMapProvider } from "leaflet-geosearch";
import "leaflet-geosearch/dist/geosearch.css";
import GarageServices from "./GarageServices";

// Default marker icon fix (Leaflet doesn't load images properly by default in CRA)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Create a custom search control component
const SearchControl = ({ setLatitude, setLongitude, setAddress }) => {
  const map = useMap();

  useEffect(() => {
    const provider = new OpenStreetMapProvider();

    const searchControl = new GeoSearchControl({
      provider,
      style: "bar",
      showMarker: true,
      showPopup: false,
      autoClose: true,
      retainZoomLevel: false,
      animateZoom: true,
      keepResult: true,
      searchLabel: "Search for an address...",
    });

    map.addControl(searchControl);

    // Handle search results
    map.on("geosearch/showlocation", (result) => {
      const { x: lng, y: lat } = result.location;
      setLatitude(lat.toFixed(6));
      setLongitude(lng.toFixed(6));

      // Reverse geocode to get address
      fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
      )
        .then((response) => response.json())
        .then((data) => {
          setAddress(data.display_name || "");
        })
        .catch((error) => {
          console.error("Error getting address:", error);
        });
    });

    // Add custom styling to the search control
    setTimeout(() => {
      const searchContainer = document.querySelector(".leaflet-geosearch-bar");
      if (searchContainer) {
        searchContainer.style.margin = "10px";
        searchContainer.style.width = "calc(100% - 20px)";

        const searchInput = searchContainer.querySelector("input");
        if (searchInput) {
          searchInput.classList.add("form-control");
          searchInput.classList.add("radius-8");
          searchInput.style.border = "1px solid #ced4da";
          searchInput.style.boxShadow = "none";
        }
      }
    }, 100);

    return () => {
      map.removeControl(searchControl);
    };
  }, [map, setLatitude, setLongitude, setAddress]);

  return null;
};

const AddGarageLayer = () => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [garageName, setGarageName] = useState("");
  const [businessRegistrationNumber, setBusinessRegistrationNumber] =
    useState("");
  const [staffData, setStaffData] = useState([
    { full_name: "", role: "", expertise: "" },
  ]);
  const [operatingHours, setOperatingHours] = useState([
    {
      day: "monday",
      open_time: "09:00",
      close_time: "18:00",
      is_closed: false,
    },
    {
      day: "tuesday",
      open_time: "09:00",
      close_time: "18:00",
      is_closed: false,
    },
    {
      day: "wednesday",
      open_time: "09:00",
      close_time: "18:00",
      is_closed: false,
    },
    {
      day: "thursday",
      open_time: "09:00",
      close_time: "18:00",
      is_closed: false,
    },
    {
      day: "friday",
      open_time: "09:00",
      close_time: "18:00",
      is_closed: false,
    },
    {
      day: "saturday",
      open_time: "09:00",
      close_time: "18:00",
      is_closed: true,
    },
    { day: "sunday", open_time: "09:00", close_time: "18:00", is_closed: true },
  ]);
  const [aboutUs, setAboutUs] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [catalogSubcategories, setCatalogSubcategories] = useState([]);
  const [catalogServices, setCatalogServices] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [emergencyService, setEmergencyService] = useState(false);
  const [towingService, setTowingService] = useState(false);
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [address, setAddress] = useState("");
  const [userImage, setUserImage] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [active, setActive] = useState(true);
  // const [offersDiagnosis, setOffersDiagnosis] = useState(false);
  // const [diagnosisFee, setDiagnosisFee] = useState("");
  // const [diagnosisFreeUntil, setDiagnosisFreeUntil] = useState("");
  const [userImagePreview, setUserImagePreview] = useState("");
  const [galleryPreviews, setGalleryPreviews] = useState([]);

  const token = localStorage.getItem("token");
  // const { mode, setMode } = useContext(ModeContext);
  const navigate = useNavigate();

  // Fetch the service catalog so garage owners can be assigned real services (by id), not free text
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/service-categories?limit=100`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const json = await response.json();

        if (!response.ok) {
          throw new Error(
            json.errorMessage || "Failed to load service categories",
          );
        }

        const categoryList = json.data?.categories || [];

        setCategories(categoryList);

        if (categoryList.length > 0) {
          setSelectedCategoryId(String(categoryList[0].id));
        }
      } catch (error) {
        console.error("Error fetching service categories:", error);
        toast.error("Failed to load service categories");
      }
    };

    fetchCategories();
  }, [token]);

  useEffect(() => {
    if (!selectedCategoryId) {
      setCatalogSubcategories([]);
      setCatalogServices([]);
      return;
    }

    let cancelled = false;

    const fetchCategoryServices = async () => {
      setCatalogLoading(true);

      try {
        const baseUrl = process.env.REACT_APP_API_URL;
        const headers = {
          Authorization: `Bearer ${token}`,
        };

        const [subRes, serviceRes] = await Promise.all([
          fetch(
            `${baseUrl}api/admin/service-subcategories?category_id=${selectedCategoryId}&page=0&limit=100`,
            { headers },
          ),
          fetch(
            `${baseUrl}api/admin/services?category_id=${selectedCategoryId}&page=0&limit=100`,
            { headers },
          ),
        ]);

        const [subJson, serviceJson] = await Promise.all([
          subRes.json(),
          serviceRes.json(),
        ]);

        if (!subRes.ok || !serviceRes.ok) {
          console.error("Subcategories response:", subJson);
          console.error("Services response:", serviceJson);
          throw new Error("Failed to load category services");
        }

        if (!cancelled) {
          setCatalogSubcategories(
            subJson.data?.subcategories ?? subJson.subcategories ?? [],
          );

          setCatalogServices(
            serviceJson.data?.services ?? serviceJson.services ?? [],
          );
        }
      } catch (error) {
        console.error("Error fetching category services:", error);

        if (!cancelled) {
          setCatalogSubcategories([]);
          setCatalogServices([]);
          toast.error("Failed to load category services");
        }
      } finally {
        if (!cancelled) {
          setCatalogLoading(false);
        }
      }
    };

    fetchCategoryServices();

    return () => {
      cancelled = true;
    };
  }, [selectedCategoryId, token]);

  const removeSelectedService = (serviceId) => {
    setSelectedServices((prev) =>
      prev.filter((service) => String(service.value) !== String(serviceId)),
    );
  };

  // Handle staff data changes
  const handleStaffChange = (index, field, value) => {
    const updatedStaff = [...staffData];
    updatedStaff[index][field] = value;
    setStaffData(updatedStaff);
  };

  // Add new staff member
  const addStaffMember = () => {
    setStaffData([...staffData, { full_name: "", role: "", expertise: "" }]);
  };

  // Remove staff member
  const removeStaffMember = (index) => {
    if (staffData.length > 1) {
      const updatedStaff = [...staffData];
      updatedStaff.splice(index, 1);
      setStaffData(updatedStaff);
    }
  };

  // Handle user image upload
  const handleUserImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUserImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setUserImage(file);
    }
  };

  function LocationMarker({ setLatitude, setLongitude, setAddress }) {
    useMapEvents({
      click(e) {
        setLatitude(e.latlng.lat.toFixed(6));
        setLongitude(e.latlng.lng.toFixed(6));

        // Reverse geocode to get address when clicking on map
        fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${e.latlng.lat}&lon=${e.latlng.lng}`,
        )
          .then((response) => response.json())
          .then((data) => {
            setAddress(data.display_name || "");
          })
          .catch((error) => {
            console.error("Error getting address:", error);
          });
      },
    });

    return null;
  }

  // Handle gallery images upload
  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map((file) => ({
      src: URL.createObjectURL(file),
      file,
    }));
    setGalleryImages((prev) => [...prev, ...newImages]);
    setGalleryPreviews((prev) => [...prev, ...newImages.map((img) => img.src)]);
    e.target.value = "";
  };

  // Remove gallery image
  const removeGalleryImage = (src) => {
    setGalleryImages((prev) => prev.filter((image) => image.src !== src));
    setGalleryPreviews((prev) => prev.filter((preview) => preview !== src));
    URL.revokeObjectURL(src);
  };

  // Handle operating hours changes
  const handleOperatingHoursChange = (day, field, value) => {
    const updatedHours = operatingHours.map((hour) => {
      if (hour.day === day) {
        return { ...hour, [field]: value };
      }
      return hour;
    });
    setOperatingHours(updatedHours);
  };

  const getPasswordError = (value) => {
    if (!value) return "Password is required";
    if (value.length < 8) return "Password must be at least 8 characters";
    if (!/[A-Z]/.test(value)) return "Add at least one uppercase letter (A-Z)";
    if (!/[a-z]/.test(value)) return "Add at least one lowercase letter (a-z)";
    if (!/[0-9]/.test(value)) return "Add at least one number (0-9)";
    if (!/[^A-Za-z0-9]/.test(value))
      return "Add at least one special character (@, #, $, !)";
    return "";
  };
  
  const passwordError = getPasswordError(password);

  const validateForm = () => {
    let errors = {};
    if (!fullName) errors.fullName = "Full name is required";
    // if (!email) errors.email = "Email is required";
    if (!aboutUs.trim()) errors.aboutUs = "About Us is required";
    if (!phoneNumber) errors.phoneNumber = "Phone number is required";
    if (passwordError) {
      errors.password = passwordError;
    }
    if (!garageName) errors.garageName = "Garage name is required";
    if (!longitude || !latitude) errors.Location = "Location is required";
    if (selectedServices.length < 1) errors.services = "Services are required";

    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach((error) => toast.error(error));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validate = await validateForm();

    if (!validate) {
      return;
    }

    const formData = new FormData();
    formData.append("fullName", fullName);
    formData.append("email", email);
    formData.append("phoneNumber", phoneNumber);
    formData.append("password", password);
    formData.append("garage_name", garageName);
    formData.append("business_registration_number", businessRegistrationNumber);
    formData.append("operating_hours", JSON.stringify(operatingHours));
    formData.append("about_us", aboutUs);
    formData.append("emergency_service", emergencyService);
    formData.append("towing_service", towingService);
    // formData.append("offers_diagnosis", String(offersDiagnosis));
    // formData.append("diagnosis_fee", offersDiagnosis ? diagnosisFee : "");
    // formData.append(
    //   "diagnosis_free_until",
    //   offersDiagnosis ? diagnosisFreeUntil : "",
    // );
    formData.append("latitude", parseFloat(latitude));
    formData.append("longitude", parseFloat(longitude));
    formData.append("address", address);
    formData.append("isActive", active);
    formData.append("staff_data", JSON.stringify(staffData));

    // Services must be sent as a JSON array of catalog service ids
    formData.append(
      "services",
      JSON.stringify(selectedServices.map((s) => s.value)),
    );

    if (userImage) {
      formData.append("user_image", userImage);
    }

    if (galleryImages.length > 0) {
      galleryImages.forEach((img) => {
        formData.append("gallery_images", img.file);
      });
    }

    const loading = toast.loading("Creating garage...");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}api/admin/garages/create`,
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const json = await response.json();

      if (response.ok) {
        toast.dismiss(loading);
        toast.success("Garage created successfully");
        navigate("/all-garages");
      } else {
        toast.dismiss(loading);
        toast.error(json.errorMessage || "Failed to create garage");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while creating the garage");
    } finally {
      toast.dismiss(loading);
    }
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-body p-40">
        <form onSubmit={handleSubmit}>
          <div className="row">
            {/* Profile Image */}
            <div className="col-12 mb-24">
              <h6 className="text-md text-primary-light mb-16">
                Profile Image
              </h6>
              <div className="mb-24 mt-16">
                <div className="avatar-upload">
                  <div className="avatar-edit position-absolute bottom-0 end-0 me-24 mt-16 z-1 cursor-pointer">
                    <input
                      type="file"
                      id="imageUpload"
                      accept=".png, .jpg, .jpeg"
                      hidden
                      onChange={handleUserImageChange}
                    />
                    <label
                      htmlFor="imageUpload"
                      className="w-32-px h-32-px d-flex justify-content-center align-items-center bg-primary-50 text-primary-600 border border-primary-600 bg-hover-primary-100 text-lg rounded-circle"
                    >
                      <Icon icon="solar:camera-outline" className="icon"></Icon>
                    </label>
                  </div>
                  <div className="avatar-preview">
                    <div
                      id="imagePreview"
                      style={{
                        backgroundImage: userImagePreview
                          ? `url(${userImagePreview})`
                          : "none",
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Basic Information */}
            <div className="col-sm-6">
              <div className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Full Name <span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  className="form-control radius-8"
                  placeholder="Enter full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="col-sm-6">
              <div className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Email
                  {/* <span className="text-danger-600">*</span> */}
                </label>
                <input
                  type="email"
                  className="form-control radius-8"
                  placeholder="Enter email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  // required
                />
              </div>
            </div>

            <div className="col-sm-6">
              <div className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Phone Number <span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  className="form-control radius-8"
                  placeholder="Enter phone number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  required
                  maxLength={13}
                />
              </div>
            </div>

            <div className="col-sm-6">
              <div className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Password <span className="text-danger-600">*</span>
                </label>
                <input
                  type="password"
                  className="form-control radius-8"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                />

                {password && passwordError && (
                  <small className="text-danger d-block mt-2">
                    {passwordError}
                  </small>
                )}

                {password && !passwordError && (
                  <small className="text-success d-flex align-items-center mt-2">
                    <Icon icon="solar:check-circle-bold" className="me-1" />
                    Password meets all requirements.
                  </small>
                )}
              </div>
            </div>

            {/* Garage Information */}
            <div className="col-sm-6">
              <div className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Garage Name <span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  className="form-control radius-8"
                  placeholder="Enter garage name"
                  value={garageName}
                  onChange={(e) => setGarageName(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="col-sm-6">
              <div className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Business Registration Number{" "}
                  <span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  className="form-control radius-8"
                  placeholder="Enter registration number"
                  value={businessRegistrationNumber}
                  onChange={(e) =>
                    setBusinessRegistrationNumber(e.target.value)
                  }
                />
              </div>
            </div>

            <div className="col-12">
              <div className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  About Us <span className="text-danger-600">*</span>
                </label>
                <textarea
                  className="form-control radius-8"
                  placeholder="Tell us about your garage"
                  value={aboutUs}
                  onChange={(e) => setAboutUs(e.target.value)}
                  rows={5}
                  required
                />
              </div>
            </div>

            {/* Services catalog */}
            <div className="col-12">
              <GarageServices
                categories={categories}
                selectedCategoryId={selectedCategoryId}
                setSelectedCategoryId={setSelectedCategoryId}
                catalogSubcategories={catalogSubcategories}
                catalogServices={catalogServices}
                catalogLoading={catalogLoading}
                selectedServices={selectedServices}
                setSelectedServices={setSelectedServices}
              />{" "}
            </div>

            {/* Selected services */}
            <div className="col-12">
              <div className="card border radius-8 mb-20 overflow-hidden">
                <div className="card-header bg-base border-bottom py-16 px-20">
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <h6 className="text-md text-primary-light mb-0">
                      Selected Services
                    </h6>

                    <span className="badge bg-primary-50 text-primary-600">
                      {selectedServices.length} selected
                    </span>
                  </div>
                </div>

                <div className="card-body p-20">
                  {selectedServices.length === 0 ? (
                    <div className="text-sm text-secondary-light">
                      No services selected. Choose services from the categories
                      above.
                    </div>
                  ) : (
                    <div className="d-flex flex-wrap gap-2">
                      {selectedServices.map((service) => (
                        <span
                          key={service.value}
                          className="badge bg-primary-50 text-primary-600 border border-primary-100 d-inline-flex align-items-center gap-2 px-12 py-10 radius-8"
                          style={{ fontSize: "13px" }}
                        >
                          {service.label}

                          <button
                            type="button"
                            className="bg-transparent border-0 p-0 d-inline-flex align-items-center text-primary-600"
                            onClick={() => removeSelectedService(service.value)}
                            title={`Remove ${service.label}`}
                            aria-label={`Remove ${service.label}`}
                          >
                            <Icon
                              icon="radix-icons:cross-2"
                              className="text-md"
                            />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Emergency / Towing capability */}
            <div className="col-sm-6">
              <div className="d-flex align-items-center gap-10 fw-medium text-lg mb-20">
                <div className="form-check style-check d-flex align-items-center">
                  <input
                    className="form-check-input radius-4 border border-neutral-500"
                    type="checkbox"
                    checked={emergencyService}
                    onChange={(e) => setEmergencyService(e.target.checked)}
                    id="emergencyService"
                  />
                </div>
                <label
                  htmlFor="emergencyService"
                  className="form-label fw-medium text-lg text-primary-light mb-0"
                >
                  Offers Emergency Service
                </label>
              </div>
            </div>

            <div className="col-sm-6">
              <div className="d-flex align-items-center gap-10 fw-medium text-lg mb-20">
                <div className="form-check style-check d-flex align-items-center">
                  <input
                    className="form-check-input radius-4 border border-neutral-500"
                    type="checkbox"
                    checked={towingService}
                    onChange={(e) => setTowingService(e.target.checked)}
                    id="towingService"
                  />
                </div>
                <label
                  htmlFor="towingService"
                  className="form-label fw-medium text-lg text-primary-light mb-0"
                >
                  Offers Towing Service
                </label>
              </div>
            </div>
            {/* Diagnosis Settings */}
            {/* <div className="col-12">
              <div className="card border radius-8 p-20 mb-20">
                <h6 className="text-md text-primary-light mb-20">
                  Diagnosis Settings
                </h6>

                <div className="row">
                  <div className="col-12 mb-20">
                    <div className="form-check d-flex align-items-center gap-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="offersDiagnosis"
                        checked={offersDiagnosis}
                        onChange={(e) => setOffersDiagnosis(e.target.checked)}
                      />
                      <label
                        className="form-check-label fw-medium"
                        htmlFor="offersDiagnosis"
                      >
                        Offers Diagnosis
                      </label>
                    </div>
                  </div>

                  {offersDiagnosis && (
                    <>
                      <div className="col-sm-6">
                        <div className="mb-20">
                          <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                            Diagnosis Fee
                          </label>
                          <input
                            type="number"
                            className="form-control radius-8"
                            placeholder="Enter diagnosis fee"
                            min="0"
                            step="0.01"
                            value={diagnosisFee}
                            onChange={(e) => setDiagnosisFee(e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="col-sm-6">
                        <div className="mb-20">
                          <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                            Diagnosis Free Until
                          </label>
                          <input
                            type="date"
                            className="form-control radius-8"
                            min={new Date().toISOString().split("T")[0]}
                            value={diagnosisFreeUntil}
                            onChange={(e) =>
                              setDiagnosisFreeUntil(e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div> */}
            {/* Location Information */}
            <div className="col-12 mb-20">
              <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                Select Location on Map{" "}
                <span className="text-danger-600">*</span>
              </label>

              <MapContainer
                center={[latitude || 37.7749, longitude || -122.4194]}
                zoom={13}
                style={{ height: "400px", width: "100%", borderRadius: "8px" }}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {latitude && longitude && (
                  <Marker
                    position={[parseFloat(latitude), parseFloat(longitude)]}
                  ></Marker>
                )}
                <LocationMarker
                  setLatitude={setLatitude}
                  setLongitude={setLongitude}
                  setAddress={setAddress}
                />
                <SearchControl
                  setLatitude={setLatitude}
                  setLongitude={setLongitude}
                  setAddress={setAddress}
                />
              </MapContainer>
              <div className="mt-2 text-muted">
                Lat: {latitude || "--"}, Lng: {longitude || "--"}
              </div>
            </div>

            <div className="col-12">
              <div className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Address <span className="text-danger-600">*</span>
                </label>
                <textarea
                  className="form-control radius-8"
                  placeholder="Enter full address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  rows={3}
                  required
                />
              </div>
            </div>

            {/* Operating Hours */}
            <div className="col-12">
              <div className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Operating Hours
                </label>
                <div className="card border radius-8 p-20">
                  {operatingHours.map((hour, index) => (
                    <div
                      key={hour.day}
                      className="row mb-15 align-items-center"
                    >
                      <div className="col-sm-2">
                        <label className="form-label text-capitalize">
                          {hour.day}
                        </label>
                      </div>
                      <div className="col-sm-2">
                        <div className="form-check d-flex align-items-center gap-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={!hour.is_closed}
                            onChange={(e) =>
                              handleOperatingHoursChange(
                                hour.day,
                                "is_closed",
                                !e.target.checked,
                              )
                            }
                            id={`open-${hour.day}`}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`open-${hour.day}`}
                          >
                            Open
                          </label>
                        </div>
                      </div>
                      <div className="col-sm-4">
                        <input
                          type="time"
                          className="form-control"
                          value={hour.open_time}
                          onChange={(e) =>
                            handleOperatingHoursChange(
                              hour.day,
                              "open_time",
                              e.target.value,
                            )
                          }
                          disabled={hour.is_closed}
                        />
                      </div>
                      <div className="col-sm-4">
                        <input
                          type="time"
                          className="form-control"
                          value={hour.close_time}
                          onChange={(e) =>
                            handleOperatingHoursChange(
                              hour.day,
                              "close_time",
                              e.target.value,
                            )
                          }
                          disabled={hour.is_closed}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Gallery Images */}
            <div className="col-12">
              <div className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Gallery Images
                </label>
                <div className="upload-image-wrapper d-flex align-items-center gap-3 flex-wrap">
                  <div className="uploaded-imgs-container d-flex gap-3 flex-wrap">
                    {galleryImages.map((image, index) => (
                      <div
                        key={index}
                        className="position-relative h-120-px w-120-px border input-form-light radius-8 overflow-hidden border-dashed bg-neutral-50"
                      >
                        <button
                          type="button"
                          className="uploaded-img__remove position-absolute top-0 end-0 z-1 text-2xxl line-height-1 me-8 mt-8 d-flex"
                          onClick={() => removeGalleryImage(image.src)}
                        >
                          <Icon
                            icon="radix-icons:cross-2"
                            className="text-xl text-danger-600"
                          ></Icon>
                        </button>
                        <img
                          className="w-100 h-100 object-fit-cover"
                          src={image.src}
                          alt="Gallery Preview"
                        />
                      </div>
                    ))}
                  </div>

                  <label
                    className="upload-file-multiple h-120-px w-120-px border input-form-light radius-8 overflow-hidden border-dashed bg-neutral-50 bg-hover-neutral-200 d-flex align-items-center flex-column justify-content-center gap-1"
                    htmlFor="gallery-upload"
                  >
                    <Icon
                      icon="solar:camera-outline"
                      className="text-xl text-secondary-light"
                    ></Icon>
                    <span className="fw-semibold text-secondary-light">
                      Upload
                    </span>
                    <input
                      id="gallery-upload"
                      type="file"
                      hidden
                      multiple
                      onChange={handleGalleryChange}
                      accept=".png, .jpg, .jpeg"
                    />
                  </label>
                </div>
              </div>
            </div>

            {/* Staff Information */}
            <div className="col-12">
              <div className="mb-20">
                <div className="d-flex justify-content-between align-items-center mb-16">
                  <h6 className="text-md text-primary-light mb-0">
                    Staff Information
                  </h6>
                  <button
                    type="button"
                    className="btn btn-primary-600 radius-8 px-20 py-11 d-flex align-items-center gap-2"
                    onClick={addStaffMember}
                  >
                    <Icon icon="material-symbols:add" />
                    Add Staff
                  </button>
                </div>

                {staffData.map((staff, index) => (
                  <div
                    key={index}
                    className="card border radius-8 p-20 mb-16 position-relative"
                  >
                    {staffData.length > 1 && (
                      <button
                        type="button"
                        className="position-absolute top-0 end-0 mt-16 me-16 text-danger-600 bg-transparent border-0"
                        onClick={() => removeStaffMember(index)}
                      >
                        <Icon
                          icon="material-symbols:close"
                          className="text-lg"
                        />
                      </button>
                    )}

                    <div className="row">
                      <div className="col-sm-4">
                        <div className="mb-20">
                          <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                            Full Name
                          </label>
                          <input
                            type="text"
                            className="form-control radius-8"
                            placeholder="Enter full name"
                            value={staff.full_name}
                            onChange={(e) =>
                              handleStaffChange(
                                index,
                                "full_name",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>

                      <div className="col-sm-4">
                        <div className="mb-20">
                          <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                            Role
                          </label>
                          <input
                            type="text"
                            className="form-control radius-8"
                            placeholder="Enter role"
                            value={staff.role}
                            onChange={(e) =>
                              handleStaffChange(index, "role", e.target.value)
                            }
                          />
                        </div>
                      </div>

                      <div className="col-sm-4">
                        <div className="mb-20">
                          <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                            Expertise
                          </label>
                          <input
                            type="text"
                            className="form-control radius-8"
                            placeholder="Enter expertise"
                            value={staff.expertise}
                            onChange={(e) =>
                              handleStaffChange(
                                index,
                                "expertise",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Active Status */}
            <div className="col-12">
              <div className="d-flex align-items-center gap-10 fw-medium text-lg mb-20">
                <div className="form-check style-check d-flex align-items-center">
                  <input
                    className="form-check-input radius-4 border border-neutral-500"
                    type="checkbox"
                    checked={active}
                    onChange={(e) => setActive(e.target.checked)}
                    id="activeStatus"
                  />
                </div>
                <label
                  htmlFor="activeStatus"
                  className="form-label fw-medium text-lg text-primary-light mb-0"
                >
                  Active
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
              <button
                type="button"
                className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                onClick={() => navigate("/all-garages")}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary-600 border border-primary-600 text-md px-56 py-12 radius-8"
              >
                Save
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddGarageLayer;
